import { useState, useCallback } from "react";
import { useAtom } from "jotai";
import { authAtom } from "@/store/authStore";
import { signUpUser } from "@/services/userService";
import type { UserSignupData, SignUpResult } from "@/types/user";
import { authService } from "@/services/authService";
import type { User } from "@/types/user";
import { supabase } from "@/libs/supabase";

export const useAuth = () => {
	const [authState, setAuthState] = useAtom(authAtom);
	const [error, setError] = useState<string | null>(null);

	const updateAuthState = useCallback(
		async (user: User | null, isAuthenticated: boolean) => {
			setAuthState({
				user,
				isAuthenticated,
				loading: false,
			});
		},
		[setAuthState],
	);

	const fetchUserProfile = useCallback(
		async (userId: string): Promise<User | null> => {
			try {
				const { data: profile, error } = await supabase
					.from("profiles")
					.select("*")
					.eq("id", userId)
					.single();

				if (error) throw error;
				return profile;
			} catch (error) {
				console.error("プロフィール取得エラー:", error);
				return null;
			}
		},
		[],
	);

	const signUp = async (
		email: string,
		password: string,
		username: string,
	): Promise<SignUpResult> => {
		setAuthState((prev) => ({ ...prev, loading: true }));
		setError(null);
		try {
			const signupData: UserSignupData = { email, password, username };
			const { user, profile } = await signUpUser(signupData);
			if (user && profile) {
				await updateAuthState(profile, true);
			}
			return { user, profile };
		} catch (err) {
			setError("サインアップに失敗しました。もう一度お試しください。");
			console.error("サインアップエラー:", err);
			throw err;
		} finally {
			setAuthState((prev) => ({ ...prev, loading: false }));
		}
	};

	const login = async (email: string, password: string) => {
		setAuthState((prev) => ({ ...prev, loading: true }));
		setError(null);
		try {
			const { user } = await authService.login(email, password);
			if (user) {
				const profile = await fetchUserProfile(user.id);
				if (profile) {
					await updateAuthState(profile, true);
				}
			}
			return user;
		} catch (err) {
			setError(
				"ログインに失敗しました。メールアドレスとパスワードを確認してください。",
			);
			console.error("ログインエラー:", err);
			throw err;
		} finally {
			setAuthState((prev) => ({ ...prev, loading: false }));
		}
	};

	const loginWithGoogle = async () => {
		setAuthState((prev) => ({ ...prev, loading: true }));
		setError(null);
		try {
			const data = await authService.loginWithGoogle();
			return data;
		} catch (err) {
			setError("Googleログインに失敗しました。もう一度お試しください。");
			console.error("Googleログインエラー:", err);
			throw err;
		} finally {
			setAuthState((prev) => ({ ...prev, loading: false }));
		}
	};

	const logout = async () => {
		setAuthState((prev) => ({ ...prev, loading: true }));
		setError(null);
		try {
			await authService.logout();
			await updateAuthState(null, false);
		} catch (err) {
			setError("ログアウトに失敗しました。");
			console.error("ログアウトエラー:", err);
			throw err;
		} finally {
			setAuthState((prev) => ({ ...prev, loading: false }));
		}
	};

	const resetPassword = async (email: string) => {
		setAuthState((prev) => ({ ...prev, loading: true }));
		setError(null);
		try {
			await authService.resetPassword(email);
			return {
				success: true,
				message: "パスワードリセットメールを送信しました。",
			};
		} catch (err) {
			setError("パスワードリセットメールの送信に失敗しました。");
			console.error("パスワードリセットエラー:", err);
			throw err;
		} finally {
			setAuthState((prev) => ({ ...prev, loading: false }));
		}
	};

	return {
		...authState,
		signUp,
		login,
		loginWithGoogle,
		logout,
		resetPassword,
		error,
		updateAuthState,
		fetchUserProfile,
	};
};
